import {FC, memo, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {CSS_VARIABLES, FlexBox, useBreakpoint, useIsMobile} from "@brg/ui";
import {Lang, Project} from "@brg/api";
import {PSLongread} from "../sections/ps-longread";
import {LayoutConst, useModal} from "../../../../core";
import {ModerationType} from "../../teams/const/ModerationTypes";
import {createPortal} from "react-dom";
import {useHistory} from "react-router-dom";
import {CourseInfoList} from "./CourseInfoList/CourseInfoList";
import { COURSE_STRUCTURE_PANEL_Z_INDEX } from "./CourseInfoList/styles";
import {useDispatch, useSelector} from "react-redux";
import {HseContextActions} from "shared/reducers/hseCurrentContext";
import {ROLES, useCoursesRole} from "shared/hooks/api/LMS/useCoursesRole";
import {CourseInfoLisActions, NULL_COUNT} from "shared/reducers/CourseInfoListSlice";
import {CourseInfoHeader} from "./CourseInfoList/components/CourseInfoHeader";
import styled from "styled-components";
import {selectorLMSModuleInfo} from "../../../reducers/LMSSlice";
import {useGetLMSDisciplines} from "../../../hooks/api/LMS/useGetLMSDisciplines";
import {getDisciplineColorFromId} from "../../../helpers/LMS/getDisciplineColorFromId";
import {useProfile} from "@brg/auth";
import {useTranslation} from "react-i18next";
import {useGetManyByListPaginated} from "../../../hooks/api/LMS/useGetManyByListPaginated";
import {useGetModuleLessons, LMSModuleLessonsInfoModel} from "shared/hooks/api/LMS/useGetModuleLessons";
import {useGetScheduleVisibility} from "shared/hooks/api/LMS/useGetScheduleVisibility";
import {useSaveScheduleVisibility} from "shared/hooks/api/LMS/useSaveScheduleVisibility";
import { isPromptModule } from "shared/pages/exploreHseContextsNew/const/promptFormsId";
import { THEME_AVAILABILITY } from "shared/constants/ThemeAvailability";
import {CreateLinkTo} from "shared/constants/linkTo";
import {convertDesignerFromGlobalToLocal} from "shared/pages/projectEdit/utils";
import {useLazyModuleThemes} from "./useLazyModuleThemes";
import {useGetHomeWorksStatisticsByThemes} from "../../../hooks/api/LMS/useGetHomeWorksStatisticsByThemes";
import {getUncheckedCount} from "../../../hooks/api/LMS/useGetHomeWorksStatistics";
import {useModuleNavigationTop} from "./useModuleNavigationTop";

const FixedTopWrapper = styled.div`
  position: fixed;
  left: 0px;
  top: var(--lms-navigation-top, calc(var(${CSS_VARIABLES.HEADER_HEIGHT}) + var(--brg-index-widget-height, 0px)));
  --lms-navigation-gap: 8px;
  z-index: 2000;
`

export const AnchorWrapper = styled(FixedTopWrapper)`
  z-index: ${COURSE_STRUCTURE_PANEL_Z_INDEX};
  @media(max-width: ${LayoutConst.screen.l}px) {
    top: auto;
    bottom: 0;
  }

  @media(max-width: ${LayoutConst.screen.s - 1}px) {
    right: 0;
  }
`

const CourseInfoWrapper = styled(FixedTopWrapper)`
  width: 100%;

  @media(max-width: ${LayoutConst.screen.md}px) {
    right: 0;
    left: auto;
  }
`

const INFO_LIST = ['theme_structure'];
const ANCHOR_BLOCK_LIST = ['course_theme', 'course_homework'];
const HEADER_ANCHOR_BLOCK_LIST = ['course_theme', 'course_lesson'];
const hasCourseRole = (sign: ROLES, role: ROLES) => role === sign

type LMSLongreadListProps = {
    project: Project;
    moduleLessons?: LMSModuleLessonsInfoModel | null;
    isModuleLessonsLoading?: boolean;
    refetchModuleLessons?: () => void;
};

const LMSLongreadList: FC<LMSLongreadListProps> = ({
    project,
    moduleLessons: moduleLessonsProp,
    isModuleLessonsLoading: isModuleLessonsLoadingProp,
    refetchModuleLessons: refetchModuleLessonsProp,
}) => {

    const lmsModuleInfo = useSelector(selectorLMSModuleInfo);
    const modal = useModal();
    const history = useHistory();
    const dispatch = useDispatch();
    const isTablet = useIsMobile();
    const outer = document.getElementById('theme-nav-portal');
    useModuleNavigationTop(outer);
    const [showHomeWork, setShowHomeWork] = useState(true);
    const [currentPost, setCurrentPost] = useState(null);
    const [themeAnchors, setThemeAnchors] = useState({});
    const [headerThemeAnchors, setHeaderThemeAnchors] = useState({});
    const [isHeaderVisible, setIsHeaderVisible] = useState(false);

    const handleHeaderVisibilityChange = useCallback((visible: boolean) => {
        setIsHeaderVisible(prev => {
            if (prev !== visible) {
                return visible;
            }
            return prev;
        });
    }, []);
    const is992 = useBreakpoint(LayoutConst.screen.m);
    const [role] = useCoursesRole(project?.projectId, project.longreadId);

    const isOwner = hasCourseRole(ROLES.teacher, role);
    const isParticipant = hasCourseRole(ROLES.student, role);
    const isJury = hasCourseRole(ROLES.jury, role);
    const hasAccess = isOwner || isParticipant || isJury;

    const isManualMode = (lmsModuleInfo as any)?.nextThemeAvailability === THEME_AVAILABILITY.Manual;

    const visibleThemeInfos = useMemo(() => {
        return lmsModuleInfo?.themeInfos || [];
    }, [lmsModuleInfo]);

    const contentThemeInfos = useMemo(() => {
        if (!isManualMode || !isParticipant) return visibleThemeInfos;
        return visibleThemeInfos.filter((t: any) => !!t.isManuallyEnabled);
    }, [visibleThemeInfos, isManualMode, isParticipant]);

    const profile = useProfile();
    const profileId = profile?.accountId;
    const { i18n } = useTranslation();

    useEffect(() => {
        dispatch(CourseInfoLisActions.setDiscipline(lmsModuleInfo?.disciplineId || ''))
    }, [lmsModuleInfo])

    // @ts-ignore
    const teams = project?.teams;

    const currentTeamId = useMemo(() => {
        if (!teams || teams?.length === 0) return '';

        return teams?.find((team: any) => team?.moderationType === ModerationType.LMS)?.teamId || ''
    }, [teams])
    const { colors: disciplineColors } = useGetLMSDisciplines(currentTeamId)

    const teacherId = profile?.designerId || profile?.accountId;
    const homeWorkStatisticsFilter = useMemo(() => ({
        projectId: project?.projectId,
        teacherId
    }), [project?.projectId, teacherId])
    const {statisticsByThemes} = useGetHomeWorksStatisticsByThemes(homeWorkStatisticsFilter, undefined, !isOwner);
    const uncheckedCountByTheme = useMemo(() => Object.entries(statisticsByThemes || {})
        .reduce<Record<string, number>>((acc, [themeId, statistic]) => {
            acc[themeId] = getUncheckedCount(statistic);
            return acc;
        }, {}), [statisticsByThemes])
    const {
        moduleLessons: moduleLessonsLocal,
        isLoading: isModuleLessonsLoadingLocal,
        refetch: refetchModuleLessonsLocal,
    } = useGetModuleLessons(moduleLessonsProp === undefined ? project?.projectId : undefined);

    const moduleLessons = moduleLessonsProp !== undefined ? moduleLessonsProp : moduleLessonsLocal;
    const isModuleLessonsLoading = moduleLessonsProp !== undefined
        ? !!isModuleLessonsLoadingProp
        : isModuleLessonsLoadingLocal;
    const refetchModuleLessons = refetchModuleLessonsProp || refetchModuleLessonsLocal;
    const { showSchedule: initialShowSchedule, settingsId, moduleId: settingsModuleId } = useGetScheduleVisibility(project?.projectId, profileId);
    const [showSchedule, setShowSchedule] = useState<boolean>(false);
    const [consultationRefreshKey, setConsultationRefreshKey] = useState(0);
    const { save: saveScheduleVisibility } = useSaveScheduleVisibility();

    useEffect(() => {
        setShowSchedule(initialShowSchedule);
    }, [initialShowSchedule]);

    const handleShowScheduleChange = useCallback((val: boolean) => {
        setShowSchedule(val);
        if (profileId) {
            saveScheduleVisibility({
                id: settingsId || project?.projectId,
                moduleId: settingsModuleId || project?.projectId || '',
                userId: profileId,
                showLessonsSchedule: val,
            });
        }
    }, [saveScheduleVisibility, settingsId, settingsModuleId, profileId, project?.projectId]);

    const courseButtons = useMemo(() => {
        return lmsModuleInfo?.buttons || []
    }, [lmsModuleInfo])

    const isLastLongreadExam = useMemo(() => Boolean(lmsModuleInfo?.examInfo?.enabled), [lmsModuleInfo])
    const longreadThemes = useMemo(() => ([...visibleThemeInfos, ...(lmsModuleInfo?.examInfo?.enabled ? [lmsModuleInfo?.examInfo] : [])]), [visibleThemeInfos, lmsModuleInfo])
    const hasLongreads = useMemo(() => (longreadThemes && !!longreadThemes?.length) , [longreadThemes])
    
    const teacherNamesById = useMemo(() => {
        const map = new Map<string, string>();

        (lmsModuleInfo?.teachers || []).forEach((teacher) => {
            const local = convertDesignerFromGlobalToLocal(teacher, i18n.language);
            const fullName = [local.surName, local.name, local.middleName].filter(Boolean).join(' ');
            if (!fullName) return;

            if (local.designerId) map.set(String(local.designerId), fullName);
            if (local.accountId) map.set(String(local.accountId), fullName);
        });

        return map;
    }, [lmsModuleInfo?.teachers, i18n.language]);

    const themeIdToCanonical = useMemo(() => {
        const map = new Map<string, string>();
        (lmsModuleInfo?.themeInfos || []).forEach((theme: any) => {
            const canonical = theme?.longreadId || theme?.id;
            if (!canonical) return;
            if (theme?.id) map.set(String(theme.id), canonical);
            if (theme?.longreadId) map.set(String(theme.longreadId), canonical);
            if (theme?.clientTempId) map.set(String(theme.clientTempId), canonical);
        });
        return map;
    }, [lmsModuleInfo?.themeInfos]);

    const moduleScheduleLessons = useMemo(() => {
        return (moduleLessons?.lessons || []).map((lesson) => {
            const rawThemeId = lesson?.themeId ? String(lesson.themeId) : '';
            const normalizedThemeId = rawThemeId
                ? (themeIdToCanonical.get(rawThemeId) || rawThemeId)
                : '';

            const withTheme = normalizedThemeId && normalizedThemeId !== lesson?.themeId
                ? { ...lesson, themeId: normalizedThemeId }
                : lesson;

            if (!withTheme?.teacherId) return withTheme;

            const teacherName = teacherNamesById.get(String(withTheme.teacherId));
            if (!teacherName) return withTheme;

            return {
                ...withTheme,
                teacherName,
            };
        });
    }, [moduleLessons, teacherNamesById, themeIdToCanonical]);

    const themeTitlesById = useMemo(() => {
        const map: Record<string, string> = {};
        (longreadThemes || []).forEach((theme: any) => {
            const id = theme?.longreadId;
            if (!id) return;
            const rawTitle = theme?.title || theme?.name;
            const localized = Lang(rawTitle, i18n.language);
            if (typeof localized === 'string' && localized) {
                map[id] = localized;
            }
        });
        return map;
    }, [longreadThemes, i18n.language]);
    
    const allLMSLgIds = useMemo(() => {
        return [...contentThemeInfos, ...(lmsModuleInfo?.examInfo?.enabled ? [lmsModuleInfo?.examInfo] : [])]
            .map((theme: any) => theme?.longreadId).filter(Boolean);
    }, [contentThemeInfos, lmsModuleInfo]);
    
    const { data: allLongreadPosts } = useGetManyByListPaginated({
        idsLongread: allLMSLgIds,
        limit: 0,
        offset: 0,
        page: 0,
        disabled: allLMSLgIds.length === 0,
        styles: ['course_theme', 'course_homework']
    });

        useEffect(() => {
            if (!allLongreadPosts?.length || !allLMSLgIds?.length) {
                return;
            }

            const postsByLongread: Record<string, any[]> = {};
            
            allLongreadPosts.forEach((post: any) => {
                const longreadId = post.longreadId;
                if (!postsByLongread[longreadId]) {
                    postsByLongread[longreadId] = [];
                }
                
                let postName = post.text || post.name || '';
                if (post.style === 'course_homework') {
                    postName = 'Домашняя работа';
                }
                
                postsByLongread[longreadId].push({
                    ...post,
                    name: postName
                });
            });

            const orderedThemeAnchors: Record<string, any[]> = {};
            const orderedHeaderAnchors: Record<string, any[]> = {};
            
            allLMSLgIds.forEach((longreadId) => {
                if (postsByLongread[longreadId]) {
                    if (isOwner) {
                        orderedThemeAnchors[longreadId] = postsByLongread[longreadId].filter((block: any) => 
                            ANCHOR_BLOCK_LIST.includes(block?.style || '')
                        );
                        orderedHeaderAnchors[longreadId] = postsByLongread[longreadId].filter((block: any) => 
                            HEADER_ANCHOR_BLOCK_LIST.includes(block?.style || '')
                        );
                    } else {
                        const filteredAnchors = postsByLongread[longreadId].filter((block: any) => 
                            ANCHOR_BLOCK_LIST.includes(block?.style || '')
                        );
                        const anchorsWithoutHomework = filteredAnchors.filter((block: any) => 
                            block?.style !== 'course_homework'
                        );
                        const theme = longreadThemes?.find((t: any) => t?.longreadId === longreadId);

                        if (theme && 'needHomeWork' in theme && theme?.needHomeWork) {
                            const homeworkBlocks = postsByLongread[longreadId].filter((block: any) =>
                                block?.style === 'homework_student' ||
                                (block?.additionalInfo?.find((info: any) => info?.name === 'student_id') &&
                                 block?.additionalInfo?.find((info: any) => info?.name === 'student_id')?.value === profileId)
                            );
                            
                            if (homeworkBlocks.length > 0) {
                                const homeworkBlock = filteredAnchors.find((block: any) => 
                                    block?.style === 'course_homework'
                                );
                                if (homeworkBlock) {
                                    anchorsWithoutHomework.push(homeworkBlock);
                                }
                            }
                        }
                        
                        orderedThemeAnchors[longreadId] = anchorsWithoutHomework;
                        orderedHeaderAnchors[longreadId] = postsByLongread[longreadId].filter((block: any) => 
                            HEADER_ANCHOR_BLOCK_LIST.includes(block?.style || '')
                        );
                    }
                }
            });
            
            setThemeAnchors(orderedThemeAnchors);
            setHeaderThemeAnchors(orderedHeaderAnchors);
        }, [allLongreadPosts, allLMSLgIds, isOwner, profileId, longreadThemes]);

    const themesWithoutExam = useMemo(() => {
        if (!longreadThemes) return [];
        return isLastLongreadExam 
            ? longreadThemes.slice(0, -1) 
            : longreadThemes;
    }, [longreadThemes, isLastLongreadExam]);
    
    const examTheme = useMemo(() => {
        if (!isLastLongreadExam || !longreadThemes?.length) return null;
        return longreadThemes[longreadThemes.length - 1];
    }, [longreadThemes, isLastLongreadExam]);
    

    const filteredThemesWithoutExam = useMemo(() => {
        const base = isLastLongreadExam ? contentThemeInfos : contentThemeInfos;
        return base.filter((i: any) => i?.longreadId) || [];
    }, [contentThemeInfos, isLastLongreadExam]);
    
    const promptModule = useMemo(() => isPromptModule(project?.projectId), [project]);

    const shouldVisibleInfoList = !promptModule;

    const onSortHomeWorkClick = useCallback((id: string) => {
        setShowHomeWork(!showHomeWork)
    },[showHomeWork])

    const onApplyExam = useCallback((id: string) => {
        modal.show(
            'send-project'
            , {
                id,
                isExam: true,
                isPreRegistration: false,
                onClose: () => modal.hide(),
            })
    },[])

    const refreshConsultations = useCallback(() => {
        refetchModuleLessons();
        setConsultationRefreshKey((value) => value + 1);
    }, [refetchModuleLessons]);

    const onRegisterConsultation = useCallback((lesson: any) => {
        modal.show('consultation-registration', {
            lesson,
            onSuccess: refreshConsultations,
        });
    }, [modal, refreshConsultations]);

    const onEditLesson = useCallback(() => {
        if (!project?.projectId) return;
        history.push(CreateLinkTo.projectEdit(project.projectId));
    }, [history, project?.projectId]);

    const onViewConsultationRecords = useCallback((lesson: any) => {
        modal.show('consultation-records', {
            lesson,
            courseGroups: moduleLessons?.courseGroups || [],
            teamId: currentTeamId,
            onSuccess: refreshConsultations,
        });
    }, [currentTeamId, modal, moduleLessons?.courseGroups, refreshConsultations]);

    // @ts-ignore
    const onSetCurrentActivePost = useCallback((id) => {
        setCurrentPost(id)
    }, [])

    const currentAnchorsRef = useRef<any[]>([]);
    const currentAnchors = useSelector((state: any) => state?.hseContext?.anchorLinks) || [];

    useEffect(() => {
        currentAnchorsRef.current = currentAnchors;
    }, [currentAnchors]);

    useEffect(() => {
        let newAnchors: any[] = [];
        if (Object.values(themeAnchors)?.length) {
            Object.entries(Object.values(themeAnchors))?.map(([k, v]) => {
                //@ts-ignore
                newAnchors = [...newAnchors, ...v]
            })
        }
        let newHeaderAnchors: any[] = [];
        if (Object.values(headerThemeAnchors)?.length) {
            Object.entries(Object.values(headerThemeAnchors))?.map(([k, v]) => {
                //@ts-ignore
                newHeaderAnchors = [...newHeaderAnchors, ...v]
            })
        }
        
        const existingAnchors = currentAnchorsRef.current || [];
        const existingPostIds = new Set(existingAnchors.map((anchor: any) => anchor.postId));
        const onlyNewAnchors = [...newAnchors, ...newHeaderAnchors].filter((anchor: any) => 
            !existingPostIds.has(anchor.postId)
        );
        
        if (onlyNewAnchors.length > 0) {
            dispatch(HseContextActions.setAnchorLinks({ 
                anchors: [...existingAnchors, ...onlyNewAnchors], 
                limit: 0 
            }));
        }
    } , [themeAnchors, headerThemeAnchors, dispatch])

    const scheduleLessonsCount = moduleScheduleLessons?.length || 0;

    useEffect(() => {
        if (lmsModuleInfo && isTablet) {
            const itemsCount = Math.max(longreadThemes?.length || NULL_COUNT, scheduleLessonsCount);
            dispatch(
                CourseInfoLisActions.setItemCount(itemsCount)
            )
        }

        return () => {
            dispatch(CourseInfoLisActions.setItemCount(NULL_COUNT))
        }
    } , [lmsModuleInfo, longreadThemes, dispatch, isTablet, scheduleLessonsCount])

    const fullLmsLongreadOrder = useMemo(() => {
        const themeIds = filteredThemesWithoutExam.map((longread) => longread?.longreadId || '');
        if (examTheme?.longreadId) {
            themeIds.push(examTheme.longreadId);
        }
        return themeIds;
    }, [filteredThemesWithoutExam, examTheme]);

    const themesToRender = useMemo(() => {
        if (!hasLongreads) return [];

        const result: Array<typeof filteredThemesWithoutExam[number] | NonNullable<typeof examTheme>> = [
            ...filteredThemesWithoutExam,
        ];
        if (examTheme) {
            result.push(examTheme);
        }
        return result;
    }, [hasLongreads, filteredThemesWithoutExam, examTheme]);

    const { visibleThemes, hasMore: hasMoreThemes, sentinelRef, getThemePagingLimit } = useLazyModuleThemes({
        themes: themesToRender,
        allLongreadPosts,
        resetKey: project?.projectId,
    });

    const ThemesBlock = useMemo(() => {
        if (!hasLongreads || themesToRender.length === 0) return null;

        return visibleThemes.map((i, index) => {
            const isExam = i?.longreadId === examTheme?.longreadId;
            const isLastTheme = index === themesToRender.length - 1;
            const themeIdAliases = [i?.longreadId, (i as any)?.id].filter((id): id is string => Boolean(id));

            return (
                <FlexBox
                    key={`${i?.longreadId}-${consultationRefreshKey}`}
                    offsetTop={(index === 0 && is992) ? -8 : 0}
                    offsetBottom={isLastTheme ? (is992 ? 24 : 32) : (is992 ? -8 : 0)}
                >
                    <PSLongread
                        lastLongreadBlockLink={isLastTheme ? lmsModuleInfo?.nextModuleLink : undefined}
                        project={project}
                        customLGId={i?.longreadId}
                        pagingLimit={getThemePagingLimit(i?.longreadId)}
                        onViewCurrentSlideScroll={onSetCurrentActivePost}
                        courseGeneralColor={getDisciplineColorFromId(lmsModuleInfo?.disciplineId || '', disciplineColors)}
                        teamId={currentTeamId}
                        onApplyExam={onApplyExam}
                        isThemeLongread
                        themeIndex={isExam ? -1 : index}
                        lmsLongreadOrder={fullLmsLongreadOrder}
                        onCheckHomework={() => window.open('/check-homework/' + project?.projectId + '?themeId=' + i?.longreadId)}
                        uncheckedHomeWorkCount={getUncheckedCount(statisticsByThemes?.[i?.longreadId || ''])}
                        courseButtons={courseButtons}
                        lmsModuleInfo={lmsModuleInfo}
                        isPromtWeekendModule={promptModule}
                        showScheduleBlocks={showSchedule}
                        moduleScheduleLessons={moduleScheduleLessons}
                        isModuleLessonsLoading={isModuleLessonsLoading}
                        themeIdAliases={themeIdAliases}
                        onEditLesson={onEditLesson}
                        onRegisterConsultation={onRegisterConsultation}
                        onViewConsultationRecords={onViewConsultationRecords}
                    />
                </FlexBox>
            );
        });
    }, [
        hasLongreads,
        visibleThemes,
        themesToRender.length,
        getThemePagingLimit,
        examTheme,
        is992,
        lmsModuleInfo,
        project,
        onSetCurrentActivePost,
        disciplineColors,
        currentTeamId,
        onApplyExam,
        fullLmsLongreadOrder,
        courseButtons,
        showSchedule,
        moduleScheduleLessons,
        isModuleLessonsLoading,
        onEditLesson,
        onRegisterConsultation,
        onViewConsultationRecords,
        consultationRefreshKey,
        promptModule,
        isParticipant,
        statisticsByThemes,
    ]);

    const canVisibleHomeWorkFilter = useMemo(() => (isOwner || isParticipant), [isOwner, isParticipant])

    const separateInfos = useMemo(() => {
        const result: any = [];

        if (hasLongreads) {
            filteredThemesWithoutExam.forEach((i: any) => {
                result.push(i);
                if (i?.needHomeWork && canVisibleHomeWorkFilter) {
                    //@ts-ignore
                    result.push({...i, isHomework: true });
                }
            });
            if (examTheme) {
                result.push({...examTheme, isExam: true });
            }
        }

        return result;
    }, [hasLongreads, filteredThemesWithoutExam, examTheme, canVisibleHomeWorkFilter])
    
    const separatedInfos = useMemo(() => {
        const result: any = [];

        if (hasLongreads) {
            longreadThemes.forEach((i: any, index: number) => {

                if (isLastLongreadExam && longreadThemes?.length - 1 === index) {
                    result.push({...i, isExam: true })
                    return;
                }

                const isManuallyHidden = isManualMode && isParticipant && !i.isManuallyEnabled;
                if (isManuallyHidden) {
                    return;
                }

                result.push(i)

                if (i?.needHomeWork && canVisibleHomeWorkFilter) {
                    //@ts-ignore
                    result.push({...i, isHomework: true })
                }
            })
        }

        return result
    }, [lmsModuleInfo, showHomeWork, canVisibleHomeWorkFilter, hasLongreads, longreadThemes, isLastLongreadExam, isManualMode, isParticipant])

    //@ts-ignore
    const filteredInfos = useMemo(() => (separatedInfos.filter((i) => showHomeWork ? i : !!i.isHomework)) , [showHomeWork, separateInfos])

    const infoList = useMemo(() => {
        //@ts-ignore
        return INFO_LIST?.reduce((a,v) => { return [...a, {title: v, data: filteredInfos }]}, [])
    } , [filteredInfos, INFO_LIST]);

    const scheduleRole = isOwner ? 'teacher' : isParticipant ? 'student' : 'none';

    const InfoList = useMemo(() => {
        if (outer) {
            return (
                createPortal(
                    <>
                    <CourseInfoWrapper>
                        <CourseInfoHeader 
                            headerThemeAnchors={headerThemeAnchors}
                            themesCount={contentThemeInfos.length}
                            hasExam={isLastLongreadExam}
                            onVisibilityChange={handleHeaderVisibilityChange}
                        />
                        </CourseInfoWrapper>
                    <AnchorWrapper>
                        {shouldVisibleInfoList && <CourseInfoList
                                //@ts-ignore
                                info={infoList}
                                anchors={themeAnchors}
                                onHmVisible={showHomeWork}
                                visibleFilter={canVisibleHomeWorkFilter}
                                onSortClick={onSortHomeWorkClick}
                                currentPost={currentPost}
                                hasHeader={isHeaderVisible}
                                scheduleConfig={{
                                    lessons: moduleScheduleLessons,
                                    role: scheduleRole,
                                    isLoading: isModuleLessonsLoading,
                                    showSchedule,
                                    onShowScheduleChange: handleShowScheduleChange,
                                }}
                                themeTitlesById={themeTitlesById}
                                uncheckedCountByTheme={uncheckedCountByTheme}
                            />}
                        </AnchorWrapper>
                        </>
                , outer)
            )
        }
    }, [
        outer,
        infoList,
        themeAnchors,
        showHomeWork,
        currentPost,
        canVisibleHomeWorkFilter,
        onSortHomeWorkClick,
        shouldVisibleInfoList,
        headerThemeAnchors,
        contentThemeInfos,
        isLastLongreadExam,
        lmsModuleInfo,
        isHeaderVisible,
        handleHeaderVisibilityChange,
        moduleScheduleLessons,
        scheduleRole,
        isModuleLessonsLoading,
        showSchedule,
        handleShowScheduleChange,
        themeTitlesById,
        uncheckedCountByTheme
    ])

    if (!hasAccess) return null;

    return (
        <>
            {ThemesBlock}
            {hasMoreThemes ? <div ref={sentinelRef} aria-hidden style={{ width: '100%', height: 1 }} /> : null}
            {outer ? InfoList : null}
        </>
    )
}

export default memo(LMSLongreadList);import {FC, memo, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {CSS_VARIABLES, FlexBox, useBreakpoint, useIsMobile} from "@brg/ui";
import {Lang, Project} from "@brg/api";
import {PSLongread} from "../sections/ps-longread";
import {LayoutConst, useModal} from "../../../../core";
import {ModerationType} from "../../teams/const/ModerationTypes";
import {createPortal} from "react-dom";
import {useHistory} from "react-router-dom";
import {CourseInfoList} from "./CourseInfoList/CourseInfoList";
import { COURSE_STRUCTURE_PANEL_Z_INDEX } from "./CourseInfoList/styles";
import {useDispatch, useSelector} from "react-redux";
import {HseContextActions} from "shared/reducers/hseCurrentContext";
import {ROLES, useCoursesRole} from "shared/hooks/api/LMS/useCoursesRole";
import {CourseInfoLisActions, NULL_COUNT} from "shared/reducers/CourseInfoListSlice";
import {CourseInfoHeader} from "./CourseInfoList/components/CourseInfoHeader";
import styled from "styled-components";
import {selectorLMSModuleInfo} from "../../../reducers/LMSSlice";
import {useGetLMSDisciplines} from "../../../hooks/api/LMS/useGetLMSDisciplines";
import {getDisciplineColorFromId} from "../../../helpers/LMS/getDisciplineColorFromId";
import {useProfile} from "@brg/auth";
import {useTranslation} from "react-i18next";
import {useGetManyByListPaginated} from "../../../hooks/api/LMS/useGetManyByListPaginated";
import {useGetModuleLessons, LMSModuleLessonsInfoModel} from "shared/hooks/api/LMS/useGetModuleLessons";
import {useGetScheduleVisibility} from "shared/hooks/api/LMS/useGetScheduleVisibility";
import {useSaveScheduleVisibility} from "shared/hooks/api/LMS/useSaveScheduleVisibility";
import { isPromptModule } from "shared/pages/exploreHseContextsNew/const/promptFormsId";
import { THEME_AVAILABILITY } from "shared/constants/ThemeAvailability";
import {CreateLinkTo} from "shared/constants/linkTo";
import {convertDesignerFromGlobalToLocal} from "shared/pages/projectEdit/utils";
import {useLazyModuleThemes} from "./useLazyModuleThemes";
import {useGetHomeWorksStatisticsByThemes} from "../../../hooks/api/LMS/useGetHomeWorksStatisticsByThemes";
import {getUncheckedCount} from "../../../hooks/api/LMS/useGetHomeWorksStatistics";
import {useModuleNavigationTop} from "./useModuleNavigationTop";

const FixedTopWrapper = styled.div`
  position: fixed;
  left: 0px;
  top: var(--lms-navigation-top, calc(var(${CSS_VARIABLES.HEADER_HEIGHT}) + var(--brg-index-widget-height, 0px)));
  --lms-navigation-gap: 8px;
  z-index: 2000;
`

export const AnchorWrapper = styled(FixedTopWrapper)`
  z-index: ${COURSE_STRUCTURE_PANEL_Z_INDEX};
  @media(max-width: ${LayoutConst.screen.l}px) {
    top: auto;
    bottom: 0;
  }

  @media(max-width: ${LayoutConst.screen.s - 1}px) {
    right: 0;
  }
`

const CourseInfoWrapper = styled(FixedTopWrapper)`
  width: 100%;

  @media(max-width: ${LayoutConst.screen.md}px) {
    right: 0;
    left: auto;
  }
`

const INFO_LIST = ['theme_structure'];
const ANCHOR_BLOCK_LIST = ['course_theme', 'course_homework'];
const HEADER_ANCHOR_BLOCK_LIST = ['course_theme', 'course_lesson'];
const hasCourseRole = (sign: ROLES, role: ROLES) => role === sign

type LMSLongreadListProps = {
    project: Project;
    moduleLessons?: LMSModuleLessonsInfoModel | null;
    isModuleLessonsLoading?: boolean;
    refetchModuleLessons?: () => void;
};

const LMSLongreadList: FC<LMSLongreadListProps> = ({
    project,
    moduleLessons: moduleLessonsProp,
    isModuleLessonsLoading: isModuleLessonsLoadingProp,
    refetchModuleLessons: refetchModuleLessonsProp,
}) => {

    const lmsModuleInfo = useSelector(selectorLMSModuleInfo);
    const modal = useModal();
    const history = useHistory();
    const dispatch = useDispatch();
    const isTablet = useIsMobile();
    const outer = document.getElementById('theme-nav-portal');
    useModuleNavigationTop(outer);
    const [showHomeWork, setShowHomeWork] = useState(true);
    const [currentPost, setCurrentPost] = useState(null);
    const [themeAnchors, setThemeAnchors] = useState({});
    const [headerThemeAnchors, setHeaderThemeAnchors] = useState({});
    const [isHeaderVisible, setIsHeaderVisible] = useState(false);

    const handleHeaderVisibilityChange = useCallback((visible: boolean) => {
        setIsHeaderVisible(prev => {
            if (prev !== visible) {
                return visible;
            }
            return prev;
        });
    }, []);
    const is992 = useBreakpoint(LayoutConst.screen.m);
    const [role] = useCoursesRole(project?.projectId, project.longreadId);

    const isOwner = hasCourseRole(ROLES.teacher, role);
    const isParticipant = hasCourseRole(ROLES.student, role);
    const isJury = hasCourseRole(ROLES.jury, role);
    const hasAccess = isOwner || isParticipant || isJury;

    const isManualMode = (lmsModuleInfo as any)?.nextThemeAvailability === THEME_AVAILABILITY.Manual;

    const visibleThemeInfos = useMemo(() => {
        return lmsModuleInfo?.themeInfos || [];
    }, [lmsModuleInfo]);

    const contentThemeInfos = useMemo(() => {
        if (!isManualMode || !isParticipant) return visibleThemeInfos;
        return visibleThemeInfos.filter((t: any) => !!t.isManuallyEnabled);
    }, [visibleThemeInfos, isManualMode, isParticipant]);

    const profile = useProfile();
    const profileId = profile?.accountId;
    const { i18n } = useTranslation();

    useEffect(() => {
        dispatch(CourseInfoLisActions.setDiscipline(lmsModuleInfo?.disciplineId || ''))
    }, [lmsModuleInfo])

    // @ts-ignore
    const teams = project?.teams;

    const currentTeamId = useMemo(() => {
        if (!teams || teams?.length === 0) return '';

        return teams?.find((team: any) => team?.moderationType === ModerationType.LMS)?.teamId || ''
    }, [teams])
    const { colors: disciplineColors } = useGetLMSDisciplines(currentTeamId)

    const teacherId = profile?.designerId || profile?.accountId;
    const homeWorkStatisticsFilter = useMemo(() => ({
        projectId: project?.projectId,
        teacherId
    }), [project?.projectId, teacherId])
    const {statisticsByThemes} = useGetHomeWorksStatisticsByThemes(homeWorkStatisticsFilter, undefined, !isOwner);
    const uncheckedCountByTheme = useMemo(() => Object.entries(statisticsByThemes || {})
        .reduce<Record<string, number>>((acc, [themeId, statistic]) => {
            acc[themeId] = getUncheckedCount(statistic);
            return acc;
        }, {}), [statisticsByThemes])
    const {
        moduleLessons: moduleLessonsLocal,
        isLoading: isModuleLessonsLoadingLocal,
        refetch: refetchModuleLessonsLocal,
    } = useGetModuleLessons(moduleLessonsProp === undefined ? project?.projectId : undefined);

    const moduleLessons = moduleLessonsProp !== undefined ? moduleLessonsProp : moduleLessonsLocal;
    const isModuleLessonsLoading = moduleLessonsProp !== undefined
        ? !!isModuleLessonsLoadingProp
        : isModuleLessonsLoadingLocal;
    const refetchModuleLessons = refetchModuleLessonsProp || refetchModuleLessonsLocal;
    const { showSchedule: initialShowSchedule, settingsId, moduleId: settingsModuleId } = useGetScheduleVisibility(project?.projectId, profileId);
    const [showSchedule, setShowSchedule] = useState<boolean>(false);
    const [consultationRefreshKey, setConsultationRefreshKey] = useState(0);
    const { save: saveScheduleVisibility } = useSaveScheduleVisibility();

    useEffect(() => {
        setShowSchedule(initialShowSchedule);
    }, [initialShowSchedule]);

    const handleShowScheduleChange = useCallback((val: boolean) => {
        setShowSchedule(val);
        if (profileId) {
            saveScheduleVisibility({
                id: settingsId || project?.projectId,
                moduleId: settingsModuleId || project?.projectId || '',
                userId: profileId,
                showLessonsSchedule: val,
            });
        }
    }, [saveScheduleVisibility, settingsId, settingsModuleId, profileId, project?.projectId]);

    const courseButtons = useMemo(() => {
        return lmsModuleInfo?.buttons || []
    }, [lmsModuleInfo])

    const isLastLongreadExam = useMemo(() => Boolean(lmsModuleInfo?.examInfo?.enabled), [lmsModuleInfo])
    const longreadThemes = useMemo(() => ([...visibleThemeInfos, ...(lmsModuleInfo?.examInfo?.enabled ? [lmsModuleInfo?.examInfo] : [])]), [visibleThemeInfos, lmsModuleInfo])
    const hasLongreads = useMemo(() => (longreadThemes && !!longreadThemes?.length) , [longreadThemes])
    
    const teacherNamesById = useMemo(() => {
        const map = new Map<string, string>();

        (lmsModuleInfo?.teachers || []).forEach((teacher) => {
            const local = convertDesignerFromGlobalToLocal(teacher, i18n.language);
            const fullName = [local.surName, local.name, local.middleName].filter(Boolean).join(' ');
            if (!fullName) return;

            if (local.designerId) map.set(String(local.designerId), fullName);
            if (local.accountId) map.set(String(local.accountId), fullName);
        });

        return map;
    }, [lmsModuleInfo?.teachers, i18n.language]);

    const themeIdToCanonical = useMemo(() => {
        const map = new Map<string, string>();
        (lmsModuleInfo?.themeInfos || []).forEach((theme: any) => {
            const canonical = theme?.longreadId || theme?.id;
            if (!canonical) return;
            if (theme?.id) map.set(String(theme.id), canonical);
            if (theme?.longreadId) map.set(String(theme.longreadId), canonical);
            if (theme?.clientTempId) map.set(String(theme.clientTempId), canonical);
        });
        return map;
    }, [lmsModuleInfo?.themeInfos]);

    const moduleScheduleLessons = useMemo(() => {
        return (moduleLessons?.lessons || []).map((lesson) => {
            const rawThemeId = lesson?.themeId ? String(lesson.themeId) : '';
            const normalizedThemeId = rawThemeId
                ? (themeIdToCanonical.get(rawThemeId) || rawThemeId)
                : '';

            const withTheme = normalizedThemeId && normalizedThemeId !== lesson?.themeId
                ? { ...lesson, themeId: normalizedThemeId }
                : lesson;

            if (!withTheme?.teacherId) return withTheme;

            const teacherName = teacherNamesById.get(String(withTheme.teacherId));
            if (!teacherName) return withTheme;

            return {
                ...withTheme,
                teacherName,
            };
        });
    }, [moduleLessons, teacherNamesById, themeIdToCanonical]);

    const themeTitlesById = useMemo(() => {
        const map: Record<string, string> = {};
        (longreadThemes || []).forEach((theme: any) => {
            const id = theme?.longreadId;
            if (!id) return;
            const rawTitle = theme?.title || theme?.name;
            const localized = Lang(rawTitle, i18n.language);
            if (typeof localized === 'string' && localized) {
                map[id] = localized;
            }
        });
        return map;
    }, [longreadThemes, i18n.language]);
    
    const allLMSLgIds = useMemo(() => {
        return [...contentThemeInfos, ...(lmsModuleInfo?.examInfo?.enabled ? [lmsModuleInfo?.examInfo] : [])]
            .map((theme: any) => theme?.longreadId).filter(Boolean);
    }, [contentThemeInfos, lmsModuleInfo]);
    
    const { data: allLongreadPosts } = useGetManyByListPaginated({
        idsLongread: allLMSLgIds,
        limit: 0,
        offset: 0,
        page: 0,
        disabled: allLMSLgIds.length === 0,
        styles: ['course_theme', 'course_homework']
    });

        useEffect(() => {
            if (!allLongreadPosts?.length || !allLMSLgIds?.length) {
                return;
            }

            const postsByLongread: Record<string, any[]> = {};
            
            allLongreadPosts.forEach((post: any) => {
                const longreadId = post.longreadId;
                if (!postsByLongread[longreadId]) {
                    postsByLongread[longreadId] = [];
                }
                
                let postName = post.text || post.name || '';
                if (post.style === 'course_homework') {
                    postName = 'Домашняя работа';
                }
                
                postsByLongread[longreadId].push({
                    ...post,
                    name: postName
                });
            });

            const orderedThemeAnchors: Record<string, any[]> = {};
            const orderedHeaderAnchors: Record<string, any[]> = {};
            
            allLMSLgIds.forEach((longreadId) => {
                if (postsByLongread[longreadId]) {
                    if (isOwner) {
                        orderedThemeAnchors[longreadId] = postsByLongread[longreadId].filter((block: any) => 
                            ANCHOR_BLOCK_LIST.includes(block?.style || '')
                        );
                        orderedHeaderAnchors[longreadId] = postsByLongread[longreadId].filter((block: any) => 
                            HEADER_ANCHOR_BLOCK_LIST.includes(block?.style || '')
                        );
                    } else {
                        const filteredAnchors = postsByLongread[longreadId].filter((block: any) => 
                            ANCHOR_BLOCK_LIST.includes(block?.style || '')
                        );
                        const anchorsWithoutHomework = filteredAnchors.filter((block: any) => 
                            block?.style !== 'course_homework'
                        );
                        const theme = longreadThemes?.find((t: any) => t?.longreadId === longreadId);

                        if (theme && 'needHomeWork' in theme && theme?.needHomeWork) {
                            const homeworkBlocks = postsByLongread[longreadId].filter((block: any) =>
                                block?.style === 'homework_student' ||
                                (block?.additionalInfo?.find((info: any) => info?.name === 'student_id') &&
                                 block?.additionalInfo?.find((info: any) => info?.name === 'student_id')?.value === profileId)
                            );
                            
                            if (homeworkBlocks.length > 0) {
                                const homeworkBlock = filteredAnchors.find((block: any) => 
                                    block?.style === 'course_homework'
                                );
                                if (homeworkBlock) {
                                    anchorsWithoutHomework.push(homeworkBlock);
                                }
                            }
                        }
                        
                        orderedThemeAnchors[longreadId] = anchorsWithoutHomework;
                        orderedHeaderAnchors[longreadId] = postsByLongread[longreadId].filter((block: any) => 
                            HEADER_ANCHOR_BLOCK_LIST.includes(block?.style || '')
                        );
                    }
                }
            });
            
            setThemeAnchors(orderedThemeAnchors);
            setHeaderThemeAnchors(orderedHeaderAnchors);
        }, [allLongreadPosts, allLMSLgIds, isOwner, profileId, longreadThemes]);

    const themesWithoutExam = useMemo(() => {
        if (!longreadThemes) return [];
        return isLastLongreadExam 
            ? longreadThemes.slice(0, -1) 
            : longreadThemes;
    }, [longreadThemes, isLastLongreadExam]);
    
    const examTheme = useMemo(() => {
        if (!isLastLongreadExam || !longreadThemes?.length) return null;
        return longreadThemes[longreadThemes.length - 1];
    }, [longreadThemes, isLastLongreadExam]);
    

    const filteredThemesWithoutExam = useMemo(() => {
        const base = isLastLongreadExam ? contentThemeInfos : contentThemeInfos;
        return base.filter((i: any) => i?.longreadId) || [];
    }, [contentThemeInfos, isLastLongreadExam]);
    
    const promptModule = useMemo(() => isPromptModule(project?.projectId), [project]);

    const shouldVisibleInfoList = !promptModule;

    const onSortHomeWorkClick = useCallback((id: string) => {
        setShowHomeWork(!showHomeWork)
    },[showHomeWork])

    const onApplyExam = useCallback((id: string) => {
        modal.show(
            'send-project'
            , {
                id,
                isExam: true,
                isPreRegistration: false,
                onClose: () => modal.hide(),
            })
    },[])

    const refreshConsultations = useCallback(() => {
        refetchModuleLessons();
        setConsultationRefreshKey((value) => value + 1);
    }, [refetchModuleLessons]);

    const onRegisterConsultation = useCallback((lesson: any) => {
        modal.show('consultation-registration', {
            lesson,
            onSuccess: refreshConsultations,
        });
    }, [modal, refreshConsultations]);

    const onEditLesson = useCallback(() => {
        if (!project?.projectId) return;
        history.push(CreateLinkTo.projectEdit(project.projectId));
    }, [history, project?.projectId]);

    const onViewConsultationRecords = useCallback((lesson: any) => {
        modal.show('consultation-records', {
            lesson,
            courseGroups: moduleLessons?.courseGroups || [],
            teamId: currentTeamId,
            onSuccess: refreshConsultations,
        });
    }, [currentTeamId, modal, moduleLessons?.courseGroups, refreshConsultations]);

    // @ts-ignore
    const onSetCurrentActivePost = useCallback((id) => {
        setCurrentPost(id)
    }, [])

    const currentAnchorsRef = useRef<any[]>([]);
    const currentAnchors = useSelector((state: any) => state?.hseContext?.anchorLinks) || [];

    useEffect(() => {
        currentAnchorsRef.current = currentAnchors;
    }, [currentAnchors]);

    useEffect(() => {
        let newAnchors: any[] = [];
        if (Object.values(themeAnchors)?.length) {
            Object.entries(Object.values(themeAnchors))?.map(([k, v]) => {
                //@ts-ignore
                newAnchors = [...newAnchors, ...v]
            })
        }
        let newHeaderAnchors: any[] = [];
        if (Object.values(headerThemeAnchors)?.length) {
            Object.entries(Object.values(headerThemeAnchors))?.map(([k, v]) => {
                //@ts-ignore
                newHeaderAnchors = [...newHeaderAnchors, ...v]
            })
        }
        
        const existingAnchors = currentAnchorsRef.current || [];
        const existingPostIds = new Set(existingAnchors.map((anchor: any) => anchor.postId));
        const onlyNewAnchors = [...newAnchors, ...newHeaderAnchors].filter((anchor: any) => 
            !existingPostIds.has(anchor.postId)
        );
        
        if (onlyNewAnchors.length > 0) {
            dispatch(HseContextActions.setAnchorLinks({ 
                anchors: [...existingAnchors, ...onlyNewAnchors], 
                limit: 0 
            }));
        }
    } , [themeAnchors, headerThemeAnchors, dispatch])

    const scheduleLessonsCount = moduleScheduleLessons?.length || 0;

    useEffect(() => {
        if (lmsModuleInfo && isTablet) {
            const itemsCount = Math.max(longreadThemes?.length || NULL_COUNT, scheduleLessonsCount);
            dispatch(
                CourseInfoLisActions.setItemCount(itemsCount)
            )
        }

        return () => {
            dispatch(CourseInfoLisActions.setItemCount(NULL_COUNT))
        }
    } , [lmsModuleInfo, longreadThemes, dispatch, isTablet, scheduleLessonsCount])

    const fullLmsLongreadOrder = useMemo(() => {
        const themeIds = filteredThemesWithoutExam.map((longread) => longread?.longreadId || '');
        if (examTheme?.longreadId) {
            themeIds.push(examTheme.longreadId);
        }
        return themeIds;
    }, [filteredThemesWithoutExam, examTheme]);

    const themesToRender = useMemo(() => {
        if (!hasLongreads) return [];

        const result: Array<typeof filteredThemesWithoutExam[number] | NonNullable<typeof examTheme>> = [
            ...filteredThemesWithoutExam,
        ];
        if (examTheme) {
            result.push(examTheme);
        }
        return result;
    }, [hasLongreads, filteredThemesWithoutExam, examTheme]);

    const { visibleThemes, hasMore: hasMoreThemes, sentinelRef, getThemePagingLimit } = useLazyModuleThemes({
        themes: themesToRender,
        allLongreadPosts,
        resetKey: project?.projectId,
    });

    const ThemesBlock = useMemo(() => {
        if (!hasLongreads || themesToRender.length === 0) return null;

        return visibleThemes.map((i, index) => {
            const isExam = i?.longreadId === examTheme?.longreadId;
            const isLastTheme = index === themesToRender.length - 1;
            const themeIdAliases = [i?.longreadId, (i as any)?.id].filter((id): id is string => Boolean(id));

            return (
                <FlexBox
                    key={`${i?.longreadId}-${consultationRefreshKey}`}
                    offsetTop={(index === 0 && is992) ? -8 : 0}
                    offsetBottom={isLastTheme ? (is992 ? 24 : 32) : (is992 ? -8 : 0)}
                >
                    <PSLongread
                        lastLongreadBlockLink={isLastTheme ? lmsModuleInfo?.nextModuleLink : undefined}
                        project={project}
                        customLGId={i?.longreadId}
                        pagingLimit={getThemePagingLimit(i?.longreadId)}
                        onViewCurrentSlideScroll={onSetCurrentActivePost}
                        courseGeneralColor={getDisciplineColorFromId(lmsModuleInfo?.disciplineId || '', disciplineColors)}
                        teamId={currentTeamId}
                        onApplyExam={onApplyExam}
                        isThemeLongread
                        themeIndex={isExam ? -1 : index}
                        lmsLongreadOrder={fullLmsLongreadOrder}
                        onCheckHomework={() => window.open('/check-homework/' + project?.projectId + '?themeId=' + i?.longreadId)}
                        uncheckedHomeWorkCount={getUncheckedCount(statisticsByThemes?.[i?.longreadId || ''])}
                        courseButtons={courseButtons}
                        lmsModuleInfo={lmsModuleInfo}
                        isPromtWeekendModule={promptModule}
                        showScheduleBlocks={showSchedule}
                        moduleScheduleLessons={moduleScheduleLessons}
                        isModuleLessonsLoading={isModuleLessonsLoading}
                        themeIdAliases={themeIdAliases}
                        onEditLesson={onEditLesson}
                        onRegisterConsultation={onRegisterConsultation}
                        onViewConsultationRecords={onViewConsultationRecords}
                    />
                </FlexBox>
            );
        });
    }, [
        hasLongreads,
        visibleThemes,
        themesToRender.length,
        getThemePagingLimit,
        examTheme,
        is992,
        lmsModuleInfo,
        project,
        onSetCurrentActivePost,
        disciplineColors,
        currentTeamId,
        onApplyExam,
        fullLmsLongreadOrder,
        courseButtons,
        showSchedule,
        moduleScheduleLessons,
        isModuleLessonsLoading,
        onEditLesson,
        onRegisterConsultation,
        onViewConsultationRecords,
        consultationRefreshKey,
        promptModule,
        isParticipant,
        statisticsByThemes,
    ]);

    const canVisibleHomeWorkFilter = useMemo(() => (isOwner || isParticipant), [isOwner, isParticipant])

    const separateInfos = useMemo(() => {
        const result: any = [];

        if (hasLongreads) {
            filteredThemesWithoutExam.forEach((i: any) => {
                result.push(i);
                if (i?.needHomeWork && canVisibleHomeWorkFilter) {
                    //@ts-ignore
                    result.push({...i, isHomework: true });
                }
            });
            if (examTheme) {
                result.push({...examTheme, isExam: true });
            }
        }

        return result;
    }, [hasLongreads, filteredThemesWithoutExam, examTheme, canVisibleHomeWorkFilter])
    
    const separatedInfos = useMemo(() => {
        const result: any = [];

        if (hasLongreads) {
            longreadThemes.forEach((i: any, index: number) => {

                if (isLastLongreadExam && longreadThemes?.length - 1 === index) {
                    result.push({...i, isExam: true })
                    return;
                }

                const isManuallyHidden = isManualMode && isParticipant && !i.isManuallyEnabled;
                if (isManuallyHidden) {
                    return;
                }

                result.push(i)

                if (i?.needHomeWork && canVisibleHomeWorkFilter) {
                    //@ts-ignore
                    result.push({...i, isHomework: true })
                }
            })
        }

        return result
    }, [lmsModuleInfo, showHomeWork, canVisibleHomeWorkFilter, hasLongreads, longreadThemes, isLastLongreadExam, isManualMode, isParticipant])

    //@ts-ignore
    const filteredInfos = useMemo(() => (separatedInfos.filter((i) => showHomeWork ? i : !!i.isHomework)) , [showHomeWork, separateInfos])

    const infoList = useMemo(() => {
        //@ts-ignore
        return INFO_LIST?.reduce((a,v) => { return [...a, {title: v, data: filteredInfos }]}, [])
    } , [filteredInfos, INFO_LIST]);

    const scheduleRole = isOwner ? 'teacher' : isParticipant ? 'student' : 'none';

    const InfoList = useMemo(() => {
        if (outer) {
            return (
                createPortal(
                    <>
                    <CourseInfoWrapper>
                        <CourseInfoHeader 
                            headerThemeAnchors={headerThemeAnchors}
                            themesCount={contentThemeInfos.length}
                            hasExam={isLastLongreadExam}
                            onVisibilityChange={handleHeaderVisibilityChange}
                        />
                        </CourseInfoWrapper>
                    <AnchorWrapper>
                        {shouldVisibleInfoList && <CourseInfoList
                                //@ts-ignore
                                info={infoList}
                                anchors={themeAnchors}
                                onHmVisible={showHomeWork}
                                visibleFilter={canVisibleHomeWorkFilter}
                                onSortClick={onSortHomeWorkClick}
                                currentPost={currentPost}
                                hasHeader={isHeaderVisible}
                                scheduleConfig={{
                                    lessons: moduleScheduleLessons,
                                    role: scheduleRole,
                                    isLoading: isModuleLessonsLoading,
                                    showSchedule,
                                    onShowScheduleChange: handleShowScheduleChange,
                                }}
                                themeTitlesById={themeTitlesById}
                                uncheckedCountByTheme={uncheckedCountByTheme}
                            />}
                        </AnchorWrapper>
                        </>
                , outer)
            )
        }
    }, [
        outer,
        infoList,
        themeAnchors,
        showHomeWork,
        currentPost,
        canVisibleHomeWorkFilter,
        onSortHomeWorkClick,
        shouldVisibleInfoList,
        headerThemeAnchors,
        contentThemeInfos,
        isLastLongreadExam,
        lmsModuleInfo,
        isHeaderVisible,
        handleHeaderVisibilityChange,
        moduleScheduleLessons,
        scheduleRole,
        isModuleLessonsLoading,
        showSchedule,
        handleShowScheduleChange,
        themeTitlesById,
        uncheckedCountByTheme
    ])

    if (!hasAccess) return null;

    return (
        <>
            {ThemesBlock}
            {hasMoreThemes ? <div ref={sentinelRef} aria-hidden style={{ width: '100%', height: 1 }} /> : null}
            {outer ? InfoList : null}
        </>
    )
}

export default memo(LMSLongreadList);